/**
 * Orders Page - Tailwind Version
 *
 * Professional real-time order management with global Header integration.
 */

import React, { useState, useEffect } from "react";
import { RefreshCw, Bell } from "lucide-react";
import { useOrdersContext } from "../../context/orders-context";
import { useRestaurantContext } from "../../context/restaurant-context";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { OrderStatus, Order } from "@tasty/api";
import { cn } from "../../lib/utils";
import OrderList from "./order-list";
import { Sheet } from "@/components/ui/sheet";
import { OrderDetailsDrawer } from "./OrderDetailsDrawer";

export default function OrdersPage() {
  const { orders, loading, fetchOrders } = useOrdersContext();
  const { restaurant } = useRestaurantContext();
  const [filter, setFilter] = useState<OrderStatus | "ALL">("ALL");

  // Drawer States
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const handler = () => fetchOrders();
    window.addEventListener("dashboard-refresh", handler as EventListener);
    return () =>
      window.removeEventListener("dashboard-refresh", handler as EventListener);
  }, [fetchOrders]);

  const countByStatus = (status: OrderStatus) =>
    orders.filter((o) => o.status === status).length;

  const filteredOrders =
    filter === "ALL" ? orders : orders.filter((o) => o.status === filter);

  const openOrderDetails = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsDrawerOpen(true);
  };

  const handleStatusChanged = (updatedOrder: Order) => {
    // Context updates itself via subscription, but we refresh anyway for safety
    fetchOrders();
  };

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-4">
    <div className="space-y-6">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black tracking-tight">Orders</h1>
            {loading && <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground/40" />}
      </div>

          <Tabs value={filter} onValueChange={(v) => setFilter(v as any)} className="w-auto">
            <TabsList className="bg-transparent p-0 h-auto gap-10 border-none">
              <TabsTrigger
                value="ALL"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all flex gap-2"
              >
                All <Badge variant="secondary" className="h-4 px-1 text-[9px] font-black">{orders.length}</Badge>
              </TabsTrigger>
              <TabsTrigger
                value={OrderStatus.NEW}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-blue-600 transition-all flex gap-2"
              >
                New <Badge className="h-4 px-1 text-[9px] bg-blue-500 font-black">{countByStatus(OrderStatus.NEW)}</Badge>
              </TabsTrigger>
              <TabsTrigger
                value={OrderStatus.COOKING}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-orange-600 transition-all flex gap-2"
              >
                In progress <Badge className="h-4 px-1 text-[9px] bg-orange-500 font-black">{countByStatus(OrderStatus.COOKING)}</Badge>
              </TabsTrigger>
              <TabsTrigger
                value={OrderStatus.READY_FOR_PICKUP}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-green-600 transition-all flex gap-2"
              >
                Ready <Badge className="h-4 px-1 text-[9px] bg-green-500 font-black">{countByStatus(OrderStatus.READY_FOR_PICKUP)}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
            </div>

        <div className="flex items-center gap-3 mb-2">
          <Button variant="outline" size="sm" onClick={fetchOrders} disabled={loading} className="h-9 px-4 rounded-lg font-bold border-muted-foreground/20">
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} /> 
            Aktualisieren
          </Button>
            </div>
      </div>

      <div className="mt-8">
        <OrderList orders={filteredOrders} loading={loading} onOrderClick={openOrderDetails} />
          </div>

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        {selectedOrderId && (
          <OrderDetailsDrawer 
            orderId={selectedOrderId}
            onClose={() => setIsDrawerOpen(false)}
            onStatusChanged={handleStatusChanged}
          />
        )}
      </Sheet>
    </div>
  );
}
